import React, { useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import ActionMenu from "../../components/Common/ActionMenu";
import RiskDrawer from "../../components/Risks/RiskDrawer";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import {
  createUpdateRisk,
  getRiskCategories,
  getRisks,
} from "../../helpers/backend_helper";

const Risk = ({ createRequest = 0 }) => {
  const [risks, setRisks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);

  const loadData = async () => {
    try {
      const [risksResponse, categoriesResponse] = await Promise.all([
        getRisks(true),
        getRiskCategories(),
      ]);
      if (!risksResponse?.status)
        throw new Error(risksResponse?.message || "Unable to load risks.");
      setRisks(risksResponse.data || []);
      setCategories(
        categoriesResponse?.status ? categoriesResponse.data || [] : []
      );
    } catch (error) {
      ErrorNotify(error.message || "Unable to load risks.");
    }
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    if (createRequest) {
      setEditingRisk(null);
      setIsDrawerOpen(true);
    }
  }, [createRequest]);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingRisk(null);
    loadData();
  };

  const saveRisk = async (values) => {
    try {
      const response = await createUpdateRisk(values);
      if (!response?.status)
        throw new Error(response?.message || "Unable to save the risk.");
      SuccessNotify(`Risk ${values.id ? "updated" : "created"} successfully.`);
    } catch (error) {
      ErrorNotify(error.message || "Unable to save the risk.");
      throw error;
    }
  };
  const filteredRisks = useMemo(
    () =>
      risks.filter(
        (risk) =>
          `${risk.code} ${risk.name} ${risk.description || ""} ${
            risk.riskCategory?.name || ""
          } ${risk.riskType}`
            .toLowerCase()
            .includes(search.toLowerCase()) &&
          (status === "all" || String(risk.status) === status)
      ),
    [risks, search, status]
  );

  const counts = useMemo(
    () => ({
      total: risks.length,
      active: risks.filter((risk) => risk.status === 1).length,
      inactive: risks.filter((risk) => risk.status !== 1).length,
      responseRequired: risks.filter((risk) => risk.requiresResponseByDefault)
        .length,
    }),
    [risks]
  );

  const columns = [
    {
      id: "code",
      header: "Code",
      accessorKey: "code",
      minSize: 135,
      flex: 0.8,
    },
    {
      id: "name",
      header: "Risk",
      accessorKey: "name",
      minSize: 245,
      flex: 1.45,
      cell: ({ row }) => (
        <div className="standards-grid__name">
          <span>{(row.original.name || "R").charAt(0)}</span>
          {row.original.name}
        </div>
      ),
    },
    {
      id: "category",
      header: "Category",
      accessorFn: (row) => row.riskCategory?.name || "—",
      minSize: 150,
      flex: 0.9,
    },
    {
      id: "riskType",
      header: "Type",
      accessorKey: "riskType",
      minSize: 105,
      flex: 0.55,
    },
    {
      id: "riskLevel",
      header: "Default Risk",
      accessorKey: "defaultRiskLevel",
      minSize: 125,
      flex: 0.65,
    },
    {
      id: "response",
      header: "Response",
      accessorFn: (row) =>
        row.requiresResponseByDefault ? "Required" : "Optional",
      minSize: 110,
      flex: 0.65,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => (row.status === 1 ? "Active" : "Inactive"),
      minSize: 95,
      flex: 0.55,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            row.original.status === 1 ? "active" : "archived"
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      minSize: 72,
      flex: 0,
      cell: ({ row }) => (
        <ActionMenu
          label={row.original.name}
          triggerClassName="masters-grid-action"
          actions={[
            {
              label: "Edit details",
              icon: <i className="bx bx-edit-alt" />,
              onSelect: () => {
                setEditingRisk(row.original);
                setIsDrawerOpen(true);
              },
            },
            ...lifecycle.actions("risk", row.original),
          ]}
        />
      ),
    },
  ];

  return (
    <section className="standards-page__content masters-list masters-risk">
      <header className="masters-list__header">
        <div>
          <h1 className="piers-page-title">
            Risks <i className="bx bx-info-circle" />
          </h1>
          <p>
            Risks define the standard risk factors available during engagement
            risk assessments.
          </p>
        </div>
      </header>

      <SummaryCards
        items={[
          [
            "bx-shield-quarter",
            "blue",
            "Total Risks",
            counts.total,
            "All configured risks",
          ],
          [
            "bx-check-circle",
            "green",
            "Active Risks",
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-pause-circle",
            "orange",
            "Inactive Risks",
            counts.inactive,
            `${
              counts.total
                ? Math.round((counts.inactive / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-message-square-detail",
            "purple",
            "Response Required",
            counts.responseRequired,
            "Risks requiring a response",
          ],
        ]}
        ariaLabel="Risk summary"
      />

      <section className="standards-grid-panel">
        
        <CustomDataGrid
 filterContent={<section className="standards-filter-panel" aria-label="Risk filters">
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search risks..."
                />
              </div>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >
              <i className="bx bx-filter-alt" /> Clear Filters
            </button>
          </div>
        </section>}

          data={filteredRisks}
          columns={columns}
          onRefresh={loadData}
          fileName="risks"
          recordLabel="risks"
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              {
                backgroundColor: "#ffffff !important",
                color: "#52627f",
              },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              {
                fontSize: 14,
                borderTopColor: "#e7ebf2",
              },
          }}
        />
      </section>
      <RiskDrawer
        categories={categories}
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        onSave={saveRisk}
        risk={editingRisk}
      />
      {lifecycle.dialog}
    </section>
  );
};

export default Risk;
